import { EnvironmentKeys } from '$/core/configuration.enum';
import { IAppConfiguration, IEnvironmentVariables } from '$/core/configuration.model';
import { AnalyticsService, MsalService } from '$/services';

export default class AppConfiguration implements IAppConfiguration {
  private cachedConfig: IEnvironmentVariables | null = null;
  /** Meant to be a unique string */
  private readonly notSetValue = Symbol('NOT_SET').toString();
  private defaultConfig: IEnvironmentVariables = Object.keys(EnvironmentKeys).reduce(
    (accumulator: IEnvironmentVariables, key: string) => {
      accumulator[key as EnvironmentKeys] = this.notSetValue;
      return accumulator;
    },
    {} as IEnvironmentVariables
  );
  private getConfigByKey(key: EnvironmentKeys): string {
    return this.cachedConfig ? this.cachedConfig[key] : this.defaultConfig[key];
  }
  /**
   * Enables tracking when configuration is ready, see main.tsx react entry point
   */
  configReady: Promise<boolean> | null = null;

  /**
   * Called as early as possible in the application lifecycle to load configuration.
   * Services that depend on configuration should not be initialized until this is complete.
   * Daisy chain init calls below as needed.
   */
  async init(): Promise<boolean> {
    // if cachedConfig is already set, skip initialization
    if (this.cachedConfig) {
      return false;
    }
    // If on localhost load config.local.json
    let path = 'config.json';
    if (window.location.hostname === 'localhost') {
      path = 'config.local.json';
    }
    try {
      const response = await fetch(`${import.meta.env.VITE_BASE_PATH || '/'}${path}`, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
      }
      this.cachedConfig = (await response.json()) as IEnvironmentVariables;
    } catch (error) {
      throw new Error('Error loading configuration');
    }
    AnalyticsService.init();
    MsalService.init();
    return true;
  }

  //#region Main Env Vars
  /** Example: https://portal-dev-api.bixbyresearch.com */
  get api(): string {
    return this.cachedConfig ? this.cachedConfig.api : this.defaultConfig.api;
  }
  /** Example: fitchgroup.co,bixbyresearch.com,creditsights.com */
  get adminDomains(): string[] {
    return this.getConfigByKey(EnvironmentKeys.adminDomains).split(',');
  }
  /** Long string, Using_this_{AG_Grid}_Enterprise_key_...b3*/
  get agGridLicense(): string {
    return this.getConfigByKey(EnvironmentKeys.agGridLicense);
  }
  get googleAnalyticsId(): string {
    return this.getConfigByKey(EnvironmentKeys.googleAnalyticsId);
  }
  get applicationInsightsKey(): string {
    return this.getConfigByKey(EnvironmentKeys.applicationInsightsKey);
  }

  get storage(): string {
    return this.getConfigByKey(EnvironmentKeys.storage);
  }
  get mailtoHelp(): string {
    return this.getConfigByKey(EnvironmentKeys.mailtoHelp);
  }
  get disableAnalytics(): string {
    return this.getConfigByKey(EnvironmentKeys.disableAnalytics);
  }
  get version(): string {
    return this.getConfigByKey(EnvironmentKeys.version);
  }
  get enableBuildNumberInUI(): boolean {
    return this.getConfigByKey(EnvironmentKeys.enableBuildNumberInUI).toLowerCase() === 'true';
  }
  //#endregion Main Env Vars
  //#endregion OAuth Configs
  get clientId(): string {
    return this.getConfigByKey(EnvironmentKeys.clientId);
  }
  get clientSecret(): string {
    return this.getConfigByKey(EnvironmentKeys.clientSecret);
  }
  //#endregion OAuth Configs
  // # region MSAL Configs
  get msalClientId(): string {
    return this.getConfigByKey(EnvironmentKeys.msalClientId);
  }
  get msalAuthority(): string {
    return this.getConfigByKey(EnvironmentKeys.msalAuthority);
  }
  get msalRedirectUri(): string {
    return this.getConfigByKey(EnvironmentKeys.msalRedirectUri);
  }
  //#endregion MSAL Configs

  // Analytics
  get heapAnalyticsAppId(): string {
    return this.getConfigByKey(EnvironmentKeys.heapAnalyticsAppId);
  }
  get useHeapAnalytics(): boolean {
    return this.getConfigByKey(EnvironmentKeys.useHeapAnalytics).toLowerCase() === 'true';
  }

  //#region Planner Configs
  // 1
  get plannerDataSitePlanId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerDataSitePlanId);
  }
  get plannerMnpiDataSitePlanId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiDataSitePlanId);
  }
  get plannerDataSiteEmailBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerDataSiteEmailBucketId);
  }
  // 2
  get plannerDataSiteMissingEmailBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerDataSiteMissingEmailBucketId);
  }
  get plannerMnpiDataSiteMissingEmailBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiDataSiteMissingEmailBucketId);
  }
  get plannerDataSiteRogueBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerDataSiteRogueBucketId);
  }
  // 3
  get plannerCallTranscriptPendingBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerCallTranscriptPendingBucketId);
  }
  get plannerCallTranscriptNeedsApprovalBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerCallTranscriptNeedsApprovalBucketId);
  }
  get plannerMnpiDataSiteEmailBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiDataSiteEmailBucketId);
  }
  // 4
  get plannerMnpiDataSiteRogueBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiDataSiteRogueBucketId);
  }
  get plannerMnpiCallTranscriptPendingBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiCallTranscriptPendingBucketId);
  }
  get plannerMnpiCallTranscriptNeedsApprovalBucketId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiCallTranscriptNeedsApprovalBucketId);
  }
  // 5
  get plannerDataSiteGroupId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerDataSiteGroupId);
  }
  get plannerMnpiDataSiteGroupId(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerMnpiDataSiteGroupId);
  }
  get plannerTaskUrl(): string {
    return this.getConfigByKey(EnvironmentKeys.plannerTaskUrl);
  }

  //#region CreditSights Configs
  get isStandAloneMode(): boolean {
    return this.getConfigByKey(EnvironmentKeys.isStandAloneMode) === 'true';
  }
  get shouldLogLocal(): boolean {
    return this.getConfigByKey(EnvironmentKeys.shouldLogLocal) === 'true';
  }
  /**
   * Required for Credit Sights CSCP integration.
   * Only used in nonStandAloneMode
   */
  get cscpUri(): string {
    return this.getConfigByKey(EnvironmentKeys.cscpUri);
  }
  /**
   * Required for Credit Sights CSCP integration.
   * Only used in StandAloneMode
   */
  get cscpIframePathPrefix(): string {
    return this.getConfigByKey(EnvironmentKeys.cscpIframePathPrefix);
  }
  /**
   * Required for Credit Sights CSCP integration.
   * Only used in StandAloneMode
   */
  get cscpWindowPathPrefix(): string {
    return this.getConfigByKey(EnvironmentKeys.cscpWindowPathPrefix);
  }
  //#endregion CreditSights Configs

  //#region Feature Flags
  // 1
  get releaseFeatureFlagPublicCompanies(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagPublicCompanies);
  }
  get releaseFeatureFlagClos(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagClos);
  }
  get releaseFeatureFlagDocumentHosting(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagDocumentHosting);
  }
  // 2
  get releaseFeatureFlagCompanyHistory(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagCompanyHistory);
  }
  get releaseFeatureFlagBixbyCompliance(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagBixbyCompliance);
  }
  get releaseFeatureFlagCreditSightsCovenantReview(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagCreditSightsCovenantReview);
  }
  // 3
  get releaseFeatureFlagCompanyComps(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagCompanyComps);
  }
  get releaseFeatureFlagCreditSightsNewsIntegration(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagCreditSightsNewsIntegration);
  }
  get releaseFeatureFlagCreditSightsTranscriptionsIntegration(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagCreditSightsTranscriptionsIntegration);
  }
  // 4
  get releaseFeatureFlagHistoricalFinancialModels(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagHistoricalFinancialModels);
  }
  get releaseFeatureFlagSFDR(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagSFDR);
  }
  get releaseFeatureFlagTreppCLO(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagTreppCLO);
  }
  // 5
  get releaseFeatureFlagCloPermissioning(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagCloPermissioning);
  }
  get releaseFeatureFlagClientProvidedDataSites(): string {
    return this.getConfigByKey(EnvironmentKeys.releaseFeatureFlagClientProvidedDataSites);
  }
  //#endregion Feature Flags

  //#region Other Configs
  get privacyPolicyUrl(): string {
    return this.getConfigByKey(EnvironmentKeys.privacyPolicyUrl);
  }
  get termsOfServiceUrl(): string {
    return this.getConfigByKey(EnvironmentKeys.termsOfServiceUrl);
  }
  get creditSightsUrl(): string {
    return this.getConfigByKey(EnvironmentKeys.creditSightsUrl);
  }
  /**
   * Safely get cutover limit, default to -1 if not set or invalid
   * */
  get cutoverLimit(): number {
    try {
      const cutoverLimit = this.getConfigByKey(EnvironmentKeys.cutoverLimit);
      return Number(cutoverLimit);
    } catch {
      return -1;
    }
  }
  //#endregion Other Configs
}
